"use client"

import { useState, useEffect, useRef } from "react"

/**
 * Hook for animating numbers with smooth counting effect
 *
 * Uses requestAnimationFrame for smooth 60fps animation and
 * an ease-out cubic curve for a natural deceleration feel.
 *
 * @param target - The final number to animate to
 * @param duration - Animation duration in milliseconds (default: 1000)
 * @param decimals - Number of decimal places to show (default: 0)
 * @param delay - Delay before animation starts in ms (default: 0)
 * @returns The current animated value
 *
 * @example
 * const animatedValue = useAnimatedNumber(95, 1200, 0)
 * // Returns 0, then animates up to 95 over 1.2 seconds
 */
export function useAnimatedNumber(
  target: number,
  duration: number = 1000,
  decimals: number = 0,
  delay: number = 0
): number {
  const [current, setCurrent] = useState(0)
  const animationRef = useRef<number | null>(null)
  const startTimeRef = useRef<number | null>(null)
  const hasAnimatedRef = useRef(false)

  useEffect(() => {
    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches

    // If reduced motion, just set the target immediately
    if (prefersReducedMotion) {
      setCurrent(target)
      return
    }

    // Reset animation state when target changes
    hasAnimatedRef.current = false
    startTimeRef.current = null

    const startAnimation = () => {
      const animate = (timestamp: number) => {
        if (!startTimeRef.current) {
          startTimeRef.current = timestamp
        }

        const elapsed = timestamp - startTimeRef.current
        const progress = Math.min(elapsed / duration, 1)

        // Ease-out cubic curve: 1 - (1 - progress)^3
        // This creates a fast start that slows down naturally
        const eased = 1 - Math.pow(1 - progress, 3)

        const newValue = eased * target
        setCurrent(newValue)

        if (progress < 1) {
          animationRef.current = requestAnimationFrame(animate)
        } else {
          // Ensure we end exactly on target
          setCurrent(target)
          hasAnimatedRef.current = true
        }
      }

      animationRef.current = requestAnimationFrame(animate)
    }

    // Start animation after delay
    const timeoutId = setTimeout(startAnimation, delay)

    return () => {
      clearTimeout(timeoutId)
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [target, duration, delay])

  // Return formatted number with specified decimals
  return Number(current.toFixed(decimals))
}

/**
 * Hook for animating currency values with smooth counting
 * Adds $ prefix and proper formatting
 *
 * @param target - The final dollar amount
 * @param duration - Animation duration in milliseconds
 * @returns Formatted currency string like "$123.45"
 */
export function useAnimatedCurrency(
  target: number,
  duration: number = 1000
): string {
  const animated = useAnimatedNumber(target, duration, 2)
  return `$${animated.toFixed(2)}`
}

/**
 * Hook for animating percentage values
 * Includes % suffix
 *
 * @param target - The final percentage (0-100)
 * @param duration - Animation duration in milliseconds
 * @returns The animated percentage number
 */
export function useAnimatedPercentage(
  target: number,
  duration: number = 1200
): number {
  return useAnimatedNumber(target, duration, 0)
}
